import Axios from 'axios';
import Cookies from 'js-cookie';

import { ACCESS_TOKEN, REFRESH_TOKEN } from 'modules/auth/consts';
import { getError } from './errors';
import { getConfig } from 'common/config';
import apiUrls from './urls';
import { clearAuthState } from './utils';

Axios.defaults.baseURL = getConfig().API_URL;

Axios.interceptors.request.use((config) => {
  const token = Cookies.get(ACCESS_TOKEN);
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
});

const refreshClient = Axios.create({
  baseURL: getConfig().API_URL,
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

Axios.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(Axios(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = Cookies.get(REFRESH_TOKEN);

        if (!refreshToken) {
          throw 'Missing refresh token';
        }

        const { data } = await refreshClient.post(apiUrls.AUTH.REFRESH_TOKEN, {
          refresh_token: refreshToken,
        });

        Cookies.set(ACCESS_TOKEN, data[ACCESS_TOKEN], {
          expires: new Date(data.expires_at),
          sameSite: 'Strict',
          secure: true,
        });
        Cookies.set(REFRESH_TOKEN, data.refresh_token, {
          expires: new Date(data.refresh_expires_at),
          sameSite: 'Strict',
          secure: true,
        });

        onRefreshed(data.access_token);

        return Axios(originalRequest);
      } catch (err) {
        clearAuthState();
        refreshSubscribers = [];

        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(getError(error));
  },
);
